/**
 * 微博面板（发布记录 + 发布弹窗，剧宣批 2026-09-26 重构）
 * 设计原型已归档（2026-10-24）：原基准 ui-concept/微博.html；现行实现见本文件 + docs/UI规范.md §2.1/§3
 * 数据源：GameState.player.postRecords（近3月发布记录，含文案/likes/reposts 快照）+ FanSystem
 * 交互：底部「发布微博」→ 弹窗选分类（营业/工作/公益）→ 工作=在播剧列表点选 → 文案预览（可换一条）→ 发布
 * 设计文档：docs/微博剧宣与粉丝改造设计.md
 */
import type { GameApp } from '../../app';
import { GameState } from '../../systems/GameState';
import { fanSystem } from '../../systems/FanSystem';
import { rankSystem } from '../../systems/RankSystem';
import { GameConfig } from '../../core/GameConfig';
import { Utils } from '../../core/Utils';
import { GameData } from '../../data/GameData';
import { Contract } from '../../data/types';
import { promoWindowOf } from '../../systems/ScriptSystem';
import { createDiv, createSpan, createButton, createBtn, showHint, showValueChanges, fillLevelBadge } from '../ui';
import { setNum } from '../feedback';
import { icon } from '../icons';
import { endingSystem } from '../../systems/EndingSystem';
import { fillAvatarSlot } from '../../avatar/gamemount';

/** 发布类型顺序来自数据表 GameData.POST_TYPES（标签即 type；文案池由 FanSystem 收口） */
const POST_TYPES = GameData.POST_TYPES;

export class WeiboPanel {
    readonly element: HTMLElement;

    private nameEl: HTMLElement;
    /** 顶部主角头像槽（67 批：微博账号头像即主角捏脸，emoji 🐦 兜底） */
    private avatarEl: HTMLElement;
    private dateBadge: HTMLElement;
    private fansCount: HTMLElement;
    private postList: HTMLElement;
    private dimOverlay: HTMLElement;
    private modalWrap: HTMLElement;
    private typeTabs: HTMLElement;
    private dramaList: HTMLElement;
    private copyText: HTMLElement;
    private postBtn: HTMLElement;
    private confirmDim: HTMLElement;
    private confirmWrap: HTMLElement;
    private currentType = '营业';
    /** 工作（剧宣）分类当前选中的剧本 id */
    private currentScriptId: string | null = null;
    private currentCopy = '';

    constructor(private app: GameApp) {
        const panel = createDiv('panel-root');
        const inner = createDiv('weibo-inner');

        // 顶部状态栏（与原型一致）
        const topBar = createDiv('top-bar');
        this.avatarEl = createDiv('avatar', ['🐦']);
        topBar.appendChild(this.avatarEl);
        const info = createDiv('top-info');
        info.appendChild(createSpan('p-name', '沈星瑶'));
        this.nameEl = info.lastElementChild as HTMLElement;
        info.appendChild(createSpan('level-badge', ''));
        this.dateBadge = createSpan('date-badge', '');  // margin-left:auto 走 .date-badge 基类（原内联同值冗余，34 批删）
        info.appendChild(this.dateBadge);
        topBar.appendChild(info);
        inner.appendChild(topBar);

        // 发布记录薄荷块（撑满剩余空间；65 批：三主页撤撕边+胶带，恢复圆角）
        const block = createDiv('mint-block list-block');
        const head = createDiv('pb-head');
        head.appendChild(createSpan('mint-block-title', '发布记录 · 近3月'));
        const fans = createDiv('pb-fans');
        fans.appendChild(createSpan(undefined, '粉丝 '));
        this.fansCount = createSpan('fans-num');
        fans.appendChild(this.fansCount);
        head.appendChild(fans);
        block.appendChild(head);
        this.postList = createDiv('post-list');
        block.appendChild(this.postList);
        inner.appendChild(block);

        // 操作按钮行：退圈（固定短宽 · 左）+ 发布微博（主按钮撑满剩余 · 右）
        const actRow = createDiv('btn-row');
        const retireBtn = createButton('btn btn-danger btn-retire', '退圈', undefined, 'logout');
        retireBtn.title = '主动退圈，直接进入结局结算';
        retireBtn.addEventListener('click', () => this.openConfirm());
        actRow.appendChild(retireBtn);
        const postBtn = createButton('btn btn-primary', '发布微博', undefined, 'send');
        postBtn.addEventListener('click', () => this.openModal());
        actRow.appendChild(postBtn);
        inner.appendChild(actRow);
        this.postBtn = postBtn;

        panel.appendChild(inner);

        // ===== 确认退圈弹窗（遮罩 + 卡片；挂 app.overlayRoot＝应用外壳，inset:0 才盖得住底部导航条——
        // 挂面板根里遮罩只到 .panel-container 为止，页签带是它的兄弟节点照常可点（2026-10-25 治「弹窗开着还能切页签」）。
        // z 档走 .dimoverlay/.modal-wrap 基类，33 批删掉过内联同值 7/10——纯冗余） =====
        this.confirmDim = createDiv('dimoverlay');
        this.app.overlayRoot.appendChild(this.confirmDim);
        this.confirmWrap = createDiv('modal-wrap');
        const confirmCard = createDiv('modal-note-card is-danger');
        const confirmTitle = createDiv('mtitle', ['确认退圈']);
        confirmTitle.insertBefore(icon('logout'), confirmTitle.firstChild);
        confirmCard.appendChild(confirmTitle);
        confirmCard.appendChild(createDiv('', ['确定要退圈吗？当前周目将结束并进入结局结算。']));
        const confirmActions = createDiv('modal-actions');
        const cancelRetireBtn = createButton('btn-cancel', '取消');
        cancelRetireBtn.addEventListener('click', () => this.closeConfirm());
        const doRetireBtn = createButton('btn-post btn-danger-red', '确认退圈');
        doRetireBtn.addEventListener('click', () => {
            this.closeConfirm();
            endingSystem.retire();
        });
        confirmActions.appendChild(cancelRetireBtn);
        confirmActions.appendChild(doRetireBtn);
        confirmCard.appendChild(confirmActions);
        this.confirmWrap.appendChild(confirmCard);
        this.app.overlayRoot.appendChild(this.confirmWrap);

        // ===== 发布弹窗（遮罩 + 卡片；挂壳理由同上） =====
        this.dimOverlay = createDiv('dimoverlay');
        this.app.overlayRoot.appendChild(this.dimOverlay);

        this.modalWrap = createDiv('modal-wrap');
        const card = createDiv('modal-note-card');
        const postTitle = createDiv('mtitle', ['发布微博']);
        postTitle.insertBefore(icon('send'), postTitle.firstChild);
        card.appendChild(postTitle);
        this.typeTabs = createDiv('type-tabs');
        card.appendChild(this.typeTabs);
        // 工作（剧宣）=在播剧选择列表（仅 type==='工作' 时显示；行样式复用 .activity-item，与行程页「播出中」同源）
        this.dramaList = createDiv('drama-pick-list');
        card.appendChild(this.dramaList);
        const copyBox = createDiv('copy-box');
        const copyLabel = createDiv('copy-label', ['文案预览']);
        // 「换一条」=纯图标钮（用户口令去文字），贴在自己作用的对象那一行右端，底部整行让给取消/发布
        const rerollBtn = createBtn('copy-reroll');
        rerollBtn.appendChild(icon('refresh'));
        rerollBtn.setAttribute('aria-label', '换一条');
        rerollBtn.addEventListener('click', () => this.rerollCopy());
        copyLabel.appendChild(rerollBtn);
        copyBox.appendChild(copyLabel);
        this.copyText = createDiv('copy-text');
        copyBox.appendChild(this.copyText);
        card.appendChild(copyBox);
        const actions = createDiv('modal-actions');
        const cancelBtn = createButton('btn-cancel', '取消');
        cancelBtn.addEventListener('click', () => this.closeModal());
        const confirmBtn = createButton('btn-post', '发布');
        confirmBtn.addEventListener('click', () => this.doPost());
        actions.appendChild(cancelBtn);
        actions.appendChild(confirmBtn);
        card.appendChild(actions);
        this.modalWrap.appendChild(card);
        this.app.overlayRoot.appendChild(this.modalWrap);

        this.element = panel;
    }

    // ===== 弹窗控制 =====

    private openModal(): void {
        // 甲案落点（2026-10-02 用户两令合成）：⛔ 记忆上次的落点；有可宣在播剧时直接落「工作」
        //（剧宣意图来了就别让人再切一签），否则落「营业」。口径走 promotableScripts()，与两处「可宣」角标同源。
        this.currentType = fanSystem.promotableScripts().length > 0 ? '工作' : '营业';
        // 默认选中第一部可宣在播剧（2026-11-01）：进「工作」即可直接发布，不再让玩家先点一部剧
        this.currentScriptId = fanSystem.promotableScripts()[0] ?? null;
        this.currentCopy = this.pickCopy();
        this.renderTypeTabs();
        this.renderDramaList();
        // 落「工作」但还没选剧：文案预览给占位句（与页签点击路径同一口径，⛔ 空白一片）
        this.copyText.textContent = this.currentCopy
            || (this.currentType === '工作' ? '（先选一部正在播出的作品）' : '');
        this.dimOverlay.classList.add('show');
        this.modalWrap.classList.add('show');
    }

    private closeModal(): void {
        this.dimOverlay.classList.remove('show');
        this.modalWrap.classList.remove('show');
    }

    // ===== 退圈确认弹窗 =====

    private openConfirm(): void {
        this.confirmDim.classList.add('show');
        this.confirmWrap.classList.add('show');
    }

    private closeConfirm(): void {
        this.confirmDim.classList.remove('show');
        this.confirmWrap.classList.remove('show');
    }

    // ===== 文案 =====

    /** 按当前分类（与工作所选剧）随机一条文案（预览与实际发布分开调用=「换一条」语义） */
    private pickCopy(): string {
        if (this.currentType === '工作') {
            const c = this.findAiring(this.currentScriptId);
            if (!c) return '';
            const script = GameData.SCRIPT_MAP[c.scriptId];
            return script ? fanSystem.rollPromoCopy(script, script.roles[c.roleIndex], c) : '';
        }
        return fanSystem.randomCopy(this.currentType, this.currentCopy);
    }

    private rerollCopy(): void {
        this.currentCopy = this.pickCopy();
        this.copyText.textContent = this.currentCopy || '（先选一部正在播出的作品）';
    }

    /** 所选剧的合同：可宣与否由 fanSystem 单一门控收口，本面板 ⛔ 自判一次 airing */
    private findAiring(scriptId: string | null): Contract | undefined {
        return fanSystem.promoContract(scriptId ?? undefined);
    }

    private renderTypeTabs(): void {
        this.typeTabs.innerHTML = '';
        // 甲案（2026-10-02）：本月有可宣在播剧时「工作」签亮「可宣」胶囊，全宣完即隐（口径走 fanSystem 单一收口）。
        // 旧 .unread-badge 只有 class 没有样式规则，「宣」是裸字挂在签上（用户令重写）⇒ 复用 .tab-badge 红底白字胶囊
        const promoLeft = fanSystem.promotableScripts().length > 0;
        // 「工作」分类的可用性只看**有没有在播剧**（2026-11-01）：有→可点（列表内自己标「本月已宣传过」）；
        // 全库没有在播剧→置灰不可点（此时工作分类无内容可展示）。可宣角标仍按未发口径挂。
        const hasAiring = fanSystem.airingContracts().length > 0;
        POST_TYPES.forEach(type => {
            const tab = createBtn(`type-tab ${type === this.currentType ? 'active' : ''}`.trim());
            tab.textContent = type;
            if (type === '工作' && promoLeft) {
                tab.appendChild(createSpan('tab-badge mint show', '可宣'));
            }
            if (type === '工作' && !hasAiring) tab.disabled = true;
            tab.addEventListener('click', () => {
                this.currentType = type;
                // 切到「工作」直接选中第一部可宣剧（与 openModal 同口径）
                this.currentScriptId = type === '工作' ? (fanSystem.promotableScripts()[0] ?? null) : null;
                this.currentCopy = this.pickCopy();
                this.renderTypeTabs();
                this.renderDramaList();
                this.copyText.textContent = this.currentCopy || (type === '工作' ? '（先选一部正在播出的作品）' : '');
            });
            this.typeTabs.appendChild(tab);
        });
    }

    /** 工作签=在播剧列表（每剧每月限 1 条；已发/未选态原地双态，⛔ 新增弹窗） */
    private renderDramaList(): void {
        this.dramaList.innerHTML = '';
        this.dramaList.style.display = this.currentType === '工作' ? '' : 'none';
        if (this.currentType !== '工作') return;
        const state = GameState.getInstance();
        const m = state.player.year * 12 + state.player.month;
        const airing = fanSystem.airingContracts();
        if (airing.length === 0) {
            const empty = createDiv('post-item post-empty-hint');
            empty.textContent = '近期没有正在播出的作品～全剧杀青开播后进这里宣传';
            this.dramaList.appendChild(empty);
            return;
        }
        airing.forEach(c => {
            const script = GameData.SCRIPT_MAP[c.scriptId];
            if (!script) return;
            const posted = state.player.postRecords.some(r => r.month === m && r.scriptId === c.scriptId);
            // 播出进度（2026-10-24 用户令）：分母=播出期月数（不是名气发放月数），
            // 走 promoWindowOf 日历窗口。2026-10-29 甲案定调：开播月整月=待播宣传月（剧在月末首滴时
            // 才上线），开播月 aired=0 标「待播」（2026-10-26 茄子令「开播期改成待播」的语义恢复），
            // 次月起「播出 1/N」——N 侧的分母仍是播出期月数。
            const win = promoWindowOf(c, script, m);
            const total = win?.airMonths ?? Math.max(1, c.airMonths ?? 1);
            const aired = win
                ? Math.min(total, Math.max(0, m - win.start))       // 开播月=待播（0），次月=播出 1/N
                : Math.max(0, total - (c.airMonthsLeft ?? 0));      // 旧档同口径
            const row = createBtn(posted ? 'activity-item locked' : 'activity-item');
            row.appendChild(createSpan('name', `《${script.name}》`));
            const meta = createSpan('meta-r');
            // 读数保留播出进度；「热度X」按用户口令删除（热度=系统侧账本，⛔ 摆到台面上让玩家盯）
            meta.appendChild(createSpan(posted ? 'cost' : 'gain',
                posted ? '本月已宣传过' : aired === 0 ? '待播' : `播出 ${aired}/${total} 月`));
            row.appendChild(meta);
            if (!posted) {
                row.addEventListener('click', () => {
                    this.currentScriptId = c.scriptId;
                    this.currentCopy = this.pickCopy();
                    this.copyText.textContent = this.currentCopy;
                    this.renderDramaList();
                });
            }
            if (this.currentScriptId === c.scriptId && !posted) row.classList.add('active');
            this.dramaList.appendChild(row);
        });
    }

    /** 发布：fanSystem.post()（收益比例化+剧宣限定校验在系统侧收口），刷新列表 */
    private doPost(): void {
        if (this.currentType === '工作' && !this.currentScriptId) {
            // 兜底：无在播剧时「工作」分类已置灰；有在播剧但本月全发完时给准确原因
            showHint(fanSystem.airingContracts().length > 0 ? '本月能宣传的剧都发过了' : '先选一部正在播出的作品');
            return;
        }
        const r = fanSystem.post(this.currentType, this.currentCopy, this.currentScriptId ?? undefined);
        if (!r) {
            showHint('本月发布已达上限（3/3）');
            return;
        }
        this.closeModal();
        this.refresh();
        // 甲案：发完剧宣同步底部「微博」角标（fanSystem.post ⛔ 不发 STATE_CHANGED，这里手动刷一次）
        this.app.updateWeiboBadge();
        // 发布成功由发布记录卡片表达，不再弹「发布成功！」；只有数值变化走数值气泡（+0 的段不显示）
        const rows: { label: string; delta: string }[] = [];
        if (r.fameGain > 0) rows.push({ label: '名气', delta: `+${r.fameGain}` });
        if (this.currentType === '工作' && r.heatGain > 0) rows.push({ label: '热度', delta: `+${r.heatGain}` });
        if (this.currentType === '公益' && r.repGain > 0) rows.push({ label: '口碑', delta: `+${r.repGain}` });
        if (rows.length) showValueChanges(rows);
    }

    // ===== 渲染 =====

    refresh(): void {
        const state = GameState.getInstance();
        const abs = state.player.year * 12 + state.player.month;

        // 顶部：姓名 + 咖位 + 日期
        this.nameEl.textContent = state.player.name;
        fillAvatarSlot(this.avatarEl, '🐦', state.player.avatar);   // 顶栏 .avatar = 3rem
        const rank = state.player.rank;
        const levelBadge = this.element.querySelector('.level-badge') as HTMLElement;
        fillLevelBadge(levelBadge, rankSystem.getRankName(rank));
        this.dateBadge.textContent = `第${state.player.year + 1}年 ${state.player.month}月`;

        // 粉丝数（v11 落盘字段；新浪口径缩写：≥1万 X.X万 / ≥1亿 X.XX亿）
        setNum(this.fansCount, fanSystem.getFanCount(), n => Utils.formatCountCN(n));

        // 发布记录（进入页面即用窗口过滤 + 倒序：过期记录不再展示）
        const records = fanSystem.getRecentPosts();
        this.postList.innerHTML = '';
        if (records.length === 0) {
            const empty = createDiv('post-item post-empty-hint');
            empty.textContent = '还没有发布过动态';
            this.postList.appendChild(empty);
            return;
        }
        records.forEach(r => {
            const month = ((r.month - 1) % 12) + 1;   // abs=year*12+month（year 从 0、month 从 1）→ 反推月须 (abs-1)%12+1
            const item = createDiv('post-item');
            const head = createDiv('post-head');
            const script = r.scriptId ? GameData.SCRIPT_MAP[r.scriptId] : undefined;
            head.appendChild(createSpan('post-tag', script ? `工作 · 《${script.name}》` : r.author ? `${r.author} · 官宣` : r.type));
            head.appendChild(createSpan('post-type', `${month}月${r.month === abs ? ' · 本月' : ''}`));
            item.appendChild(head);
            item.appendChild(createDiv('post-text', [r.text]));
            const meta = createDiv('post-meta');
            // likes/reposts 读发布时落盘快照（⛔ 现算——旧「刷新变数」伪公式已随 v11 删除）
            const likesSpan = createSpan('icon-row');
            likesSpan.appendChild(icon('like'));
            likesSpan.appendChild(createSpan('likes', Utils.formatCountCN(r.likes)));
            const rtsSpan = createSpan('icon-row');
            rtsSpan.appendChild(icon('repost'));
            rtsSpan.appendChild(createSpan('retweets', Utils.formatCountCN(r.reposts)));
            meta.appendChild(likesSpan);
            meta.appendChild(rtsSpan);
            item.appendChild(meta);
            this.postList.appendChild(item);
        });
    }

    destroy(): void {
        // 无外部事件监听
    }
}
